"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormItemProps,
  FormLabel,
  FormMessage,
} from "../form";

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

interface FormRadioGroupProps<T extends string> extends FormItemProps<
  typeof RadioGroup,
  T
> {
  options: { label: string; value: T; disabled?: boolean }[];
}

function FormRadioGroupBase<T extends string>(
  props: FormRadioGroupProps<T>,
  ref: React.ForwardedRef<React.ElementRef<typeof RadioGroup>>
) {
  return (
    <FormItem className="space-y-3">
      <FormLabel>{props.label}</FormLabel>
      <RadioGroup
        onValueChange={props.onChange}
        defaultValue={props.value}
        className="flex gap-6 flex-row"
        ref={ref}
      >
        {props.options.map((option) => (
          <FormItem
            key={option.value}
            className="flex flex-row items-center space-x-2 space-y-0"
          >
            <FormControl>
              <RadioGroupItem disabled={option.disabled} value={option.value} />
            </FormControl>
            <FormLabel
              className={cn("font-normal", option.disabled && "opacity-50")}
            >
              {option.label}
            </FormLabel>
          </FormItem>
        ))}
      </RadioGroup>
      <FormDescription>{props.description}</FormDescription>
      <FormMessage />
    </FormItem>
  );
}

const FormRadioGroup = React.forwardRef(FormRadioGroupBase) as <
  T extends string,
>(
  props: FormRadioGroupProps<T> & {
    ref?: React.Ref<React.ElementRef<typeof RadioGroup>>;
  }
) => React.ReactElement;

export { RadioGroup, RadioGroupItem, FormRadioGroup };
